import React, { useState, useEffect, useRef, useCallback } from 'react';
import lumiObserver from './lumiObserver';
import { createInterpreter } from './lumiInterpreter';

const T = {
  bg: '#0D1117',
  surface: '#161B22',
  border: '#30363D',
  text: '#E6EDF3',
  textMid: '#8B949E',
  accent: '#0B8A7E',
  correct: '#10B981',
  wrong: '#EF4444',
  hint: '#F59E0B',
};

// Existing /api/chat proxy — configurable via env so dev/prod can point at
// localhost or the deployed Railway server.
const CHAT_ENDPOINT =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_CHAT_ENDPOINT) ||
  '/api/chat';

// ── Strict system prompt — calibrated to refuse stock responses ────────
const LUMI_IN_INTERACTIVE_SYSTEM = `You are Lumi, an Australian tutor sitting next to a student during an interactive learning activity. You can see exactly what they're doing on screen — you have the full event log.

CRITICAL RULES — YOU MUST FOLLOW THESE OR YOU FAIL YOUR JOB:
1. NEVER open with "Hi", "Hey", "Hello", "Great question", "I'd love to help", "Good try", "Nice work", or any greeting. You're already in the middle of a tutoring session.
2. NEVER ask "what are you working on?" or "where are you stuck?" — the event log already tells you. Asking means you didn't read it.
3. NEVER give filler encouragement — no "you've got this", "keep going", "you're doing great".
4. ALWAYS reference a specific action the student just took. If they tried u = x and it failed, your first clause mentions u = x (not "your substitution").
5. Maximum 3 sentences unless the student explicitly asks for a longer explanation.
6. Ask AT MOST one question per message. Do not produce bullet lists of suggestions.
7. No emoji in every message — reserve ✨ for genuine celebration moments only.
8. Australian English: "maths", "colour", "mum", "practise" (verb) / "practice" (noun). Never "math" or "mom".
9. If the student's year level is Year 11 or 12 HSC, speak at HSC exam depth — do not dumb it down.
10. NEVER reveal the correct solution unless the student has explicitly said they give up AND you've first asked them to name what they think the blocker is.

DIAGNOSIS PROCESS:
- Read the event log. What was the most recent action?
- Is there a pattern in the last 3–5 events? Same kind of mistake? Circling? On the right track but arithmetic off?
- Say ONE thing that helps them move forward. Refer to specific numbers, names, or inputs from the log.

HANDLING "i give up" / "I don't know" / "just tell me":
- First reply: "Before I show you — tell me what you think the blocker is. Sometimes naming it unsticks it." (not verbatim, but this idea)
- Only if they insist after that, give the answer with clear working.

HANDLING "hi" / "hello" / stock openers from the student:
- Do NOT reciprocate. Instead, comment on what's on screen. Example: "You're halfway through the second challenge — the temperature slider is still at 400 K. Want me to walk through what moving it down would do?"

RESPONSE STYLE:
- Conversational, warm, concrete. A human tutor who's been watching quietly.
- Direct. If they're circling, say "you're circling". If they're about to get it, say "one more step".
- Call out misconceptions directly without softening.`;

// ── API call ───────────────────────────────────────────────────────────
async function callLumi({
  interactiveTitle, currentProblem, correctSolution,
  yearLevel, curriculum,
  recentEvents, trigger, triggerDetail,
  studentMessage, chatHistory,
}) {
  const userContent = [
    `Interactive: ${interactiveTitle}`,
    `Current problem: ${currentProblem}`,
    `Correct solution (for your reference — do NOT reveal unless student gives up after being prompted to name the blocker): ${correctSolution || '(not provided)'}`,
    '',
    `Student year level: ${yearLevel || '(unknown)'}`,
    `Curriculum: ${curriculum || '(unknown)'}`,
    '',
    `Last ${recentEvents.length} events (oldest first):`,
    JSON.stringify(recentEvents, null, 2),
    '',
    `Trigger: ${trigger}`,
    triggerDetail ? `Trigger detail: ${JSON.stringify(triggerDetail)}` : '',
    studentMessage ? `Student just said: "${studentMessage}"` : '',
    '',
    'Respond as Lumi. Max 3 sentences unless they asked for a long explanation. Reference specific actions from the event log. Do NOT begin with a greeting.',
  ].filter(Boolean).join('\n');

  const messages = [
    ...(chatHistory || []).slice(-6).map(m => ({ role: m.role, content: m.text })),
    { role: 'user', content: userContent },
  ];

  const body = {
    model: 'claude-haiku-5-5',
    max_tokens: 400,
    system: LUMI_IN_INTERACTIVE_SYSTEM,
    messages,
  };

  const res = await fetch(CHAT_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`chat API ${res.status}`);
  const data = await res.json();
  const text = data?.content?.[0]?.text || data?.completion || data?.error || '';
  return text.trim();
}

// ── Telemetry sink ────────────────────────────────────────────────────
// Lightweight client-side logger. POSTs to /api/telemetry/lumi-tutor if
// the endpoint exists; otherwise falls back to console.debug so we at
// least have the data in the browser devtools during beta.
function logIntervention(record) {
  try {
    const payload = { ...record, ts: Date.now() };
    // Best-effort; swallow errors silently.
    fetch('/api/telemetry/lumi-tutor', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[LumiTutor]', record);
    }
  } catch (_) { /* noop */ }
}

// ── Component ─────────────────────────────────────────────────────────
export default function LumiTutor({
  interactiveId,
  interactiveTitle,
  currentProblem,
  correctSolution,
  yearLevel = 'Year 12',
  curriculum = 'NSW HSC',
  placement = 'right',     // 'right' | 'inline'
  defaultCollapsed = false,
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [messages, setMessages] = useState([]);   // { role, text, trigger, unread }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(false);
  const threadRef = useRef(null);
  const autoScrollRef = useRef(true);
  const lastProactiveStateRef = useRef(null);

  // Arm idle timers once the panel is mounted; clear on unmount.
  useEffect(() => {
    lumiObserver.armIdleTimers(interactiveId);
    return () => lumiObserver.disarmIdleTimers(interactiveId);
  }, [interactiveId]);

  // Build the API call context from current state.
  const buildApiContext = useCallback((extra = {}) => {
    return {
      interactiveTitle,
      currentProblem,
      correctSolution,
      yearLevel,
      curriculum,
      recentEvents: lumiObserver.getRecentForInteractive(interactiveId, 20),
      chatHistory: messages.map(m => ({ role: m.role, text: m.text })),
      ...extra,
    };
  }, [interactiveId, interactiveTitle, currentProblem, correctSolution, yearLevel, curriculum, messages]);

  // ── Trigger handler (from interpreter) ───────────────────────────────
  const handleTrigger = useCallback(async (triggerPayload, event) => {
    const { trigger, detail } = triggerPayload;
    const isStudentMessage = trigger === 'STUDENT_MESSAGE';
    const studentMessage = isStudentMessage ? (detail?.text || '') : null;

    // 800ms delay on proactive messages so it doesn't feel robotic.
    const delay = isStudentMessage ? 350 : 800;

    setLoading(true);
    await new Promise(r => setTimeout(r, delay));

    const t0 = Date.now();
    try {
      const text = await callLumi({
        ...buildApiContext({ trigger, triggerDetail: detail, studentMessage }),
      });

      // Replace any prior unread proactive message with the new one so
      // we never show more than one unread proactive at a time.
      setMessages(prev => {
        let next = prev;
        if (!isStudentMessage) {
          next = prev.filter(m => !(m.role === 'assistant' && m.unread && m.proactive));
        }
        next = [...next, { role: 'assistant', text, trigger, proactive: !isStudentMessage, unread: !isStudentMessage }];
        return next;
      });

      if (!isStudentMessage) {
        setPulse(true);
        setTimeout(() => setPulse(false), 1800);
        if (collapsed) setCollapsed(false);
      }

      logIntervention({
        interactiveId,
        trigger,
        proactiveOrResponse: isStudentMessage ? 'response' : 'proactive',
        lumiMessage: text,
        apiLatencyMs: Date.now() - t0,
      });
    } catch (err) {
      // Silent failure — no toast, just a gentle fallback bubble.
      setMessages(prev => [
        ...prev,
        { role: 'assistant', text: "I lost the thread for a second — try that again or tell me what you tried.", trigger: 'error', unread: false },
      ]);
      logIntervention({ interactiveId, trigger, error: err.message });
    } finally {
      setLoading(false);
    }
  }, [buildApiContext, collapsed, interactiveId]);

  // Subscribe the interpreter.
  useEffect(() => {
    const interp = createInterpreter({ interactiveId, onTrigger: handleTrigger });
    return () => interp.dispose();
  }, [interactiveId, handleTrigger]);

  // Student message path — bypasses rules engine.
  const sendStudentMessage = () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setMessages(prev => [...prev, { role: 'user', text: trimmed }]);
    setInput('');
    lumiObserver.emit({
      interactiveId,
      type: 'student_message',
      payload: { text: trimmed },
    });
  };

  // Mark messages as read when the panel is open and the user looks at them.
  useEffect(() => {
    if (collapsed) return;
    const t = setTimeout(() => {
      setMessages(prev => prev.some(m => m.unread)
        ? prev.map(m => ({ ...m, unread: false }))
        : prev);
    }, 600);
    return () => clearTimeout(t);
  }, [collapsed, messages.length]);

  // Auto-scroll behavior: only stick to bottom if the user was already
  // at the bottom. Don't yank them away from re-reading earlier context.
  useEffect(() => {
    if (!threadRef.current) return;
    if (autoScrollRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const onThreadScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    autoScrollRef.current = atBottom;
  };

  // ── UI ──────────────────────────────────────────────────────────────
  const panelStyle = placement === 'right'
    ? {
        position: 'fixed', right: 16, top: 16, bottom: 16, width: 360,
        zIndex: 40,
      }
    : {
        position: 'relative', width: '100%', maxWidth: 360, height: 520,
      };

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        aria-label="Open Lumi tutor"
        style={{
          position: 'fixed', right: 20, bottom: 20, zIndex: 40,
          width: 52, height: 52, borderRadius: 26, border: 'none',
          background: T.accent, color: '#fff',
          boxShadow: pulse ? `0 0 0 0 ${T.accent}88, 0 8px 24px #0008` : '0 8px 24px #0008',
          animation: pulse ? 'lumiPulse 1.2s ease-out infinite' : 'none',
          cursor: 'pointer', fontSize: 22,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        ✨
        <style>{`@keyframes lumiPulse {
          0% { box-shadow: 0 0 0 0 ${T.accent}66, 0 8px 24px #0008; }
          70% { box-shadow: 0 0 0 18px ${T.accent}00, 0 8px 24px #0008; }
          100% { box-shadow: 0 0 0 0 ${T.accent}00, 0 8px 24px #0008; }
        }`}</style>
      </button>
    );
  }

  return (
    <div role="complementary" aria-label="Lumi in-interactive tutor" style={{
      ...panelStyle,
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 14, display: 'flex', flexDirection: 'column',
      fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.text,
      boxShadow: pulse ? `0 0 0 2px ${T.accent}, 0 12px 40px #000a` : '0 12px 40px #000a',
      transition: 'box-shadow .3s',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px', borderBottom: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span aria-hidden="true" style={{ fontSize: 16 }}>✨</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800 }}>Lumi</div>
            <div style={{ fontSize: 10, color: T.textMid }}>Watching while you work</div>
          </div>
        </div>
        <button onClick={() => setCollapsed(true)} aria-label="Collapse Lumi" style={{
          background: 'transparent', color: T.textMid, border: 'none', cursor: 'pointer',
          fontSize: 18, lineHeight: 1,
        }}>–</button>
      </div>

      {/* Thread */}
      <div ref={threadRef} onScroll={onThreadScroll} style={{
        flex: 1, overflowY: 'auto', padding: '12px',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        {messages.length === 0 && (
          <div style={{ color: T.textMid, fontSize: 12, lineHeight: 1.5 }}>
            I'm reading along. I'll only speak up when I see something worth mentioning, or you can type to me any time.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.role === 'assistant' ? 'flex-start' : 'flex-end',
            maxWidth: '85%',
            padding: '8px 12px', borderRadius: 12,
            background: m.role === 'assistant' ? T.bg : T.accent,
            color: m.role === 'assistant' ? T.text : '#fff',
            border: m.role === 'assistant' ? `1px solid ${T.border}` : 'none',
            fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap',
          }}>{m.text}</div>
        ))}
        {loading && (
          <div style={{
            alignSelf: 'flex-start', padding: '8px 12px',
            background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12,
            fontSize: 12, color: T.textMid,
          }}>
            <span style={{ animation: 'lumiDots 1.4s infinite' }}>thinking</span>
            <style>{`@keyframes lumiDots { 0% { opacity: .4 } 50% { opacity: 1 } 100% { opacity: .4 } }`}</style>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{
        padding: 10, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 6,
      }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendStudentMessage(); } }}
          placeholder="Type a question…"
          aria-label="Message to Lumi"
          style={{
            flex: 1, padding: '8px 10px', borderRadius: 8,
            background: T.bg, border: `1px solid ${T.border}`, color: T.text,
            fontSize: 13, outline: 'none',
          }}
        />
        <button
          onClick={sendStudentMessage}
          disabled={loading || !input.trim()}
          style={{
            padding: '8px 12px',
            background: (loading || !input.trim()) ? T.border : T.accent,
            color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700,
            cursor: (loading || !input.trim()) ? 'not-allowed' : 'pointer', fontSize: 13,
          }}>Send</button>
      </div>
    </div>
  );
}
